const _ = require("lodash");
const Font = require("../../util/Font");
const {
	findArguments,
	makeFormattedText,
	makeMathCommandNode,
	makeNonmathCommandNode,
} = require("../util/ParserHelpers");
const {getLineWidth} = require("../util/RenderHelpers");
const {toLatin, ONES} = require("../../../../shared/Numbers");

// Note: using seperate functions for clarity, but organized
//       bold, italic, and underline into one file since they're
//       all text formatting

/**
 * Makes a parser that, in non-math mode, breaks words up into their respective
 * chunks.
 *
 * @param {string}  fontType The font being used.
 * @param {boolean} mathMode Whether to make the math mode or regular parser.
 *
 * @returns The parser.
 */
const segmentParserMake = (mathMode, fontType) => {
	const makeCommandNode = mathMode ? makeMathCommandNode : makeFormattedText;
	return function(ps) {
		const args = findArguments(ps, 1);
		const psList = mathMode ? args : args[0];
		return makeCommandNode(fontType, psList);
	};
};

const unitedParserMake = (mathMode, fontType = "underline") => {
	const makeCommandNode = mathMode
		? makeMathCommandNode
		: makeNonmathCommandNode;
	return function(ps) {
		return makeCommandNode(fontType, findArguments(ps, 1));
	};
};

const makeNode = fontType => {
	const fontOptions = {};
	fontOptions[fontType] = true;
	return {
		draw(rc, x, y, node, measure) {
			return rc.drawNode(rc, x, y, node.children[0]);
		},
		measure(rc, fontInfo, props, node) {
			const newFontInfo = _.extend({}, fontInfo, fontOptions);
			return rc.measureNode(rc, newFontInfo, props, node.children[0]);
		},
		readAloud: (node, {raProps, readAloudBatexNode}) =>
			readAloudBatexNode(node.children[0], raProps),
	};
};

const readAloudAsOneNode = {
	draw(rc, x, y, node, measure) {
		return rc.drawNode(rc, x, y, node.children[0]);
	},
	measure(rc, fontInfo, props, node) {
		return rc.measureNode(rc, fontInfo, props, node.children[0]);
	},
	readAloud: (node, {raProps, readAloudBatexNode}) =>
		readAloudBatexNode(node.children[0], raProps)
			// replace spaces and commas with nothing
			.replace(/\s|[,]/g, "")
			.replace(/^\d+$/, match => toLatin(match)),
};

const readAloudAsDigitsNode = {
	draw(rc, x, y, node, measure) {
		return rc.drawNode(rc, x, y, node.children[0]);
	},
	measure(rc, fontInfo, props, node) {
		return rc.measureNode(rc, fontInfo, props, node.children[0]);
	},
	readAloud: (node, {raProps, readAloudBatexNode}) =>
		readAloudBatexNode(node.children[0], raProps)
			// replace spaces and commas with nothing
			.replace(/\d/g, n => ONES[Number(n)] + " "),
};

const getLineNode = function(nodeType) {
	const isUnderline = nodeType === "underline";

	const nodeData = {
		draw(rc, x, y, node, measure) {
			const context = rc.context;
			const {bottom, fontInfo, top, width} = measure;
			rc.drawNode(rc, x, y, node.children[0]);
			context.save();
			context.font = Font.fromInfo(fontInfo);
			context.lineWidth = getLineWidth(rc);
			context.beginPath();
			const lineY = isUnderline ? y + bottom : y - top;
			context.moveTo(x, lineY);
			context.lineTo(x + width, lineY);
			context.stroke();
			context.restore();
		},
		measure(rc, fontInfo, props, node) {
			const childSize = rc.measureNode(rc, fontInfo, props, node.children[0]);
			const location = isUnderline ? "bottom" : "top";
			childSize[location] += getLineWidth(rc, fontInfo);
			return childSize;
		},
	};
	nodeData.readAloud = isUnderline
		? (node, {raProps, readAloudBatexNode}) =>
				readAloudBatexNode(node.children[0], raProps)
		: {children: true, suffix: "repeating"};
	return nodeData;
};

module.exports = {
	parsers: {
		nonmathCommands: {
			bold: segmentParserMake(false, "bold"),
			italic: segmentParserMake(false, "italic"),

			// don't break children up here (underline has to go under all elements, raAsOne needs to join all elemetns)
			underline: unitedParserMake(false),
			raAsOne: unitedParserMake(false, "raAsOne"),
			raAsDigits: unitedParserMake(false, "raAsDigits"),
		},
		mathCommands: {
			bold: segmentParserMake(true, "bold"),
			italic: segmentParserMake(true, "italic"),
			overbar: segmentParserMake(true, "overbar"),

			// don't break children up here (underline has to go under all elements, raAsOne needs to join all elemetns)
			underline: unitedParserMake(true),
			raAsOne: unitedParserMake(true, "raAsOne"),
			raAsDigits: unitedParserMake(false, "raAsDigits"),
		},
	},
	nodes: {
		bold: makeNode("bold"),
		italic: makeNode("italic"),
		overbar: getLineNode("overbar"),
		underline: getLineNode("underline"),
		raAsOne: readAloudAsOneNode,
		raAsDigits: readAloudAsDigitsNode,
	},
};
